import React, { useState } from "react";
import Layout from "@/components/Layout";
import Seo, { useLocalBusinessLd } from "@/components/Seo";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, Navigation } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function Contact() {
  const s = useSiteSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/contact", form); toast.success("Message envoyé !"); setForm({ name: "", email: "", phone: "", message: "" }); }
    catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };
  return (
    <Layout>
      <Seo title="Contact | NJ Conciergerie Rouen"
        description="Contactez NJ Conciergerie à Rouen : 06 66 93 42 67, contact@laconciergerierouen.com, 22 boulevard de la Marne, 76000 Rouen."
        canonical="/contact" jsonLd={useLocalBusinessLd()} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">Contact</span>
        <h1 className="font-serif font-light text-4xl sm:text-5xl text-nj-text mt-3">Parlons de votre projet</h1>
        <div className="grid lg:grid-cols-2 gap-12 mt-10">
          <div>
            <div className="space-y-5">
              <a href={`tel:${s.phone_intl}`} className="flex items-center gap-4 group" data-testid="contact-phone">
                <div className="w-12 h-12 rounded-sm bg-nj-olive flex items-center justify-center"><Phone className="w-5 h-5 text-nj-primary" /></div>
                <div><div className="text-xs uppercase tracking-wider text-nj-muted">Téléphone</div><div className="font-semibold text-nj-text group-hover:text-nj-primary">{s.phone}</div></div>
              </a>
              <a href={`mailto:${s.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                <div className="w-12 h-12 rounded-sm bg-nj-olive flex items-center justify-center"><Mail className="w-5 h-5 text-nj-primary" /></div>
                <div><div className="text-xs uppercase tracking-wider text-nj-muted">Email</div><div className="font-semibold text-nj-text group-hover:text-nj-primary">{s.email}</div></div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-nj-olive flex items-center justify-center"><MapPin className="w-5 h-5 text-nj-primary" /></div>
                <div><div className="text-xs uppercase tracking-wider text-nj-muted">Adresse</div><div className="font-semibold text-nj-text">{s.address}</div><div className="text-sm text-nj-muted mt-0.5">{s.hours}</div></div>
              </div>
            </div>
            <div className="mt-8 rounded-md overflow-hidden border border-nj-olive aspect-[16/10]">
              <iframe title="Carte NJ Conciergerie" width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                src="https://www.openstreetmap.org/export/embed.html?bbox=1.06%2C49.42%2C1.13%2C49.46&layer=mapnik&marker=49.44%2C1.099" />
            </div>
            <a href="https://www.google.com/maps/dir/?api=1&destination=22+boulevard+de+la+Marne+Rouen" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-nj-primary font-medium hover:gap-3 transition-all">
              <Navigation className="w-4 h-4" /> Itinéraire
            </a>
          </div>
          <form onSubmit={submit} className="bg-white border border-nj-olive rounded-md p-7 space-y-4" data-testid="contact-form">
            <div><Label>Nom *</Label><Input required data-testid="contact-name" value={form.name} onChange={set("name")} className="mt-1.5" /></div>
            <div><Label>Email *</Label><Input required type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} className="mt-1.5" /></div>
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
            <div><Label>Message *</Label><Textarea required rows={5} data-testid="contact-message" value={form.message} onChange={set("message")} className="mt-1.5" /></div>
            <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full bg-nj-primary hover:bg-nj-primaryHover text-white rounded-sm py-6">
              {loading ? "Envoi…" : "Envoyer"}
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
